import React, { useState } from 'react';
import { useAuth } from '../lib/auth';
import { Navigate } from '@tanstack/react-router';
import { useTransactions, useTransactionStats } from '../hooks/useTransactions';
import { useAccounts } from '../hooks/useAccounts';
import { TransactionForm } from '../components/transactions/TransactionForm';
import { TransactionRow } from '../components/transactions/TransactionRow';
import { Navigation } from '../components/layout/Navigation';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { formatCurrency } from '../lib/currency';
import { useTranslation } from 'react-i18next';

export function Transactions() {
    const { t } = useTranslation();
    const { user, isAuthenticated, isLoading: authLoading } = useAuth();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [accountFilter, setAccountFilter] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');

    const filters = {
        account_id: accountFilter ? parseInt(accountFilter) : undefined,
        type: typeFilter || undefined,
        category: categoryFilter || undefined,
    };

    const { data: transactions, isLoading } = useTransactions(filters);
    const { data: stats } = useTransactionStats();
    const { data: accounts } = useAccounts();

    if (authLoading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-gray-500">{t('common:loading')}</div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" />;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navigation />
            <div className="max-w-6xl mx-auto p-6">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900">{t('transactions:title')}</h1>
                    <p className="text-gray-600 mt-1">{t('transactions:subtitle')}</p>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <Card>
                            <div className="text-sm text-gray-600 mb-1">{t('transactions:statistics.total_income')}</div>
                            <div className="text-2xl font-bold text-green-600">
                                {formatCurrency(stats.total_income)}
                            </div>
                        </Card>
                        <Card>
                            <div className="text-sm text-gray-600 mb-1">{t('transactions:statistics.total_expense')}</div>
                            <div className="text-2xl font-bold text-red-600">
                                {formatCurrency(stats.total_expense)}
                            </div>
                        </Card>
                        <Card>
                            <div className="text-sm text-gray-600 mb-1">{t('transactions:statistics.net_balance')}</div>
                            <div className={`text-2xl font-bold ${
                                stats.net_balance >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                                {formatCurrency(stats.net_balance)}
                            </div>
                        </Card>
                    </div>
                )}

                {/* Filters and Add Button */}
                <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                    <div className="flex flex-col md:flex-row gap-4 mb-4">
                        <select
                            value={accountFilter}
                            onChange={(e) => setAccountFilter(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">{t('transactions:filter_by_account')}</option>
                            {accounts?.map((account) => (
                                <option key={account.id} value={account.id}>
                                    {account.name}
                                </option>
                            ))}
                        </select>

                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">{t('transactions:filter_by_type')}</option>
                            <option value="income">{t('transactions:types.income')}</option>
                            <option value="expense">{t('transactions:types.expense')}</option>
                        </select>

                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">{t('transactions:filter_by_category')}</option>
                            <option value="salary">{t('transactions:categories.salary')}</option>
                            <option value="freelance">{t('transactions:categories.freelance')}</option>
                            <option value="business">{t('transactions:categories.business')}</option>
                            <option value="investment">{t('transactions:categories.investment')}</option>
                            <option value="other_income">{t('transactions:categories.other_income')}</option>
                            <option value="food">{t('transactions:categories.food')}</option>
                            <option value="transport">{t('transactions:categories.transport')}</option>
                            <option value="shopping">{t('transactions:categories.shopping')}</option>
                            <option value="entertainment">{t('transactions:categories.entertainment')}</option>
                            <option value="bills">{t('transactions:categories.bills')}</option>
                            <option value="healthcare">{t('transactions:categories.healthcare')}</option>
                            <option value="education">{t('transactions:categories.education')}</option>
                            <option value="other_expense">{t('transactions:categories.other_expense')}</option>
                        </select>

                        <div className="ml-auto">
                            <Button onClick={() => setIsModalOpen(true)} variant="primary">
                                {t('transactions:add_transaction')}
                            </Button>
                        </div>
                    </div>

                    {(accountFilter || typeFilter || categoryFilter) && (
                        <button
                            onClick={() => {
                                setAccountFilter('');
                                setTypeFilter('');
                                setCategoryFilter('');
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700"
                        >
                            {t('transactions:clear_filters')}
                        </button>
                    )}
                </div>

                {/* Transactions List */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    {isLoading ? (
                        <div className="p-8 text-center text-gray-500">{t('common:loading')}</div>
                    ) : transactions && transactions.length > 0 ? (
                        <div>
                            {transactions.map((transaction) => (
                                <TransactionRow key={transaction.id} transaction={transaction} />
                            ))}
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-500">
                            <p className="mb-4">{t('transactions:empty_state')}</p>
                            <Button onClick={() => setIsModalOpen(true)} variant="primary">
                                {t('transactions:add_transaction')}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Add Transaction Modal */}
                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={t('transactions:add_transaction')}
                >
                    <TransactionForm onSuccess={() => setIsModalOpen(false)} />
                </Modal>
            </div>
        </div>
    );
}
